import type { FC, MouseEvent, KeyboardEvent, MutableRefObject } from 'react';
import React, { useCallback, useContext, useRef } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';

import { AppNavigationContext } from '@confluence/app-navigation-context';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { CREATE_SPACE_DIALOG } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { fg } from '@confluence/feature-gating';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useCreateSpaceStoreActions } from '../CreateSpaceDrawer/CreateSpaceStore.tsx';
import { useCanCreateSpace } from '../useCanCreateSpace.tsx';

import { CreateSpaceStandardButtonLoading } from './CreateSpaceStandardButton.tsx';
import { CreateSpaceMenuItemNav3WithIcon } from './CreateSpaceMenuItemNav3WithIcon.tsx';
import { i18n } from './createSpaceButtonI18n.tsx';

const CreateSpaceMenuItem = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CreateSpaceMenuItem" */ './CreateSpaceMenuItem.tsx'))
			.CreateSpaceMenuItem,
});

const CreateSpaceMenuItemNav3 = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CreateSpaceMenuItemNav3" */ './CreateSpaceMenuItemNav3.tsx'
			)
		).CreateSpaceMenuItemNav3,
});

const CreateSpaceStandardButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CreateSpaceStandardButton" */ './CreateSpaceStandardButton.tsx'
			)
		).CreateSpaceStandardButton,
	loading: () => <CreateSpaceStandardButtonLoading />,
});

const CreateSpacePlusButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CreateSpacePlusButton" */ './CreateSpacePlusButton.tsx'
			)
		).CreateSpacePlusButton,
});

type CreateSpaceAppearance =
	| 'default'
	| 'link'
	| 'plus'
	| 'menu-item'
	| 'menu-item-nav3'
	| 'menu-item-nav3-with-icon'
	| 'compact'
	| 'link-button';

export type CreateSpaceDisplayItemProps = {
	onClick: (e: MouseEvent | KeyboardEvent) => void;
	onMouseDown: () => void;
};

export type CreateSpaceButtonProps = {
	appearance: CreateSpaceAppearance;
	source: string;
	onClick?: () => void;
	customFocusRef?: MutableRefObject<HTMLElement | null>;
	icon?: React.ReactChild;
};

export const CreateSpaceButton: FC<CreateSpaceButtonProps> = ({
	appearance,
	source,
	onClick,
	customFocusRef,
	icon,
}) => {
	const { isRovoLLMEnabled } = useSessionData();
	const { canCreateSpace } = useCanCreateSpace();
	const { openDrawer } = useCreateSpaceStoreActions();
	const { openModal } = useSmartCreationModalActions();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'create-space-button',
	});
	const { resetStickySearchRef } = useContext(AppNavigationContext);
	const createSpaceButtonRef = useRef<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const preloadCreateSpace = () => {
		void preloadWebItemLocation({
			location: CREATE_SPACE_DIALOG,
		});
	};

	const handleCreate = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			resetStickySearchRef?.current?.();
			const location = appearance === 'menu-item-nav3-with-icon' ? 'topCreateNav' : undefined;

			if (isRovoLLMEnabled && fg('aifc_space_creation_enabled')) {
				openModal(source, '');
			} else {
				openDrawer(customFocusRef || createSpaceButtonRef, location);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSpaceButton',
					source,
					attributes: {
						location,
					},
				},
			}).fire();
			e.stopPropagation();
			onClick?.();
		},
		[
			resetStickySearchRef,
			appearance,
			openDrawer,
			openModal,
			customFocusRef,
			createAnalyticsEvent,
			source,
			onClick,
			isRovoLLMEnabled,
		],
	);

	const handleClick = useCallback(
		(e: MouseEvent | KeyboardEvent) => enforceStorageLimit(handleCreate)(e),
		[enforceStorageLimit, handleCreate],
	);

	if (!canCreateSpace) {
		return null;
	}

	if (appearance === 'menu-item') {
		return <CreateSpaceMenuItem onClick={handleClick} onMouseDown={preloadCreateSpace} />;
	} else if (appearance === 'menu-item-nav3') {
		return <CreateSpaceMenuItemNav3 onClick={handleClick} onMouseDown={preloadCreateSpace} />;
	} else if (appearance === 'menu-item-nav3-with-icon') {
		return (
			<CreateSpaceMenuItemNav3WithIcon onClick={handleClick} onMouseDown={preloadCreateSpace} />
		);
	} else if (appearance === 'compact') {
		return (
			<CreateSpaceStandardButton
				onClick={handleClick}
				onMouseDown={preloadCreateSpace}
				appearance="default"
				spacing="compact"
				createSpaceButtonRef={createSpaceButtonRef}
				icon={icon}
			/>
		);
	} else if (appearance === 'plus') {
		return (
			<CreateSpacePlusButton
				onClick={handleClick}
				onMouseDown={preloadCreateSpace}
				createSpaceButtonRef={createSpaceButtonRef}
			/>
		);
	} else if (appearance === 'link-button') {
		return (
			<Button
				id="create-space-button-next"
				onClick={handleClick}
				onMouseDown={preloadCreateSpace}
				appearance="link"
				aria-haspopup="dialog"
				ref={createSpaceButtonRef}
				iconAfter={icon}
			>
				<FormattedMessage {...i18n.createSpaceLinkButton} />
			</Button>
		);
	}

	return (
		<CreateSpaceStandardButton
			onClick={handleClick}
			onMouseDown={preloadCreateSpace}
			appearance={appearance}
			spacing="default"
			createSpaceButtonRef={createSpaceButtonRef}
			icon={icon}
		/>
	);
};
